import type {Dayjs} from 'dayjs';

/**
 * 揽收 POP 审核 —— 前端类型定义
 *
 * 与 api-server `api/graphql/popReview/types/PopReviewTypes.kt` 一一对应。
 * 后端 Long 型 ID 一律以 String 传输（避免 JS 精度丢失），时间为 epoch seconds。
 */

/** 审核结论；后端为通用引擎态，展示文案在前端转换（APPROVED→合格，REJECTED→不合格） */
export enum PopReviewStatus {
    PENDING_REVIEW = 'PENDING_REVIEW',
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    CANCELLED = 'CANCELLED'
}

/** 入审场景；工作台一级页签，查询时原样传给后端 reviewScene */
export enum PopReviewScene {
    PICKUP_SUCCESS = 'PICKUP_SUCCESS',
    PICKUP_FAILED = 'PICKUP_FAILED'
}

/** 工作台二级 Tab（仅前端 UI 状态；查询时转为 isFinished 传给后端） */
export enum PopReviewTabType {
    /** 待审核 */
    NEW = 'NEW',
    /** 已审核：审核已结束（合格 / 不合格 / 已取消） */
    TERMINATED = 'TERMINATED'
}

/** Tab → 查询条件；后端按 pop_review.is_finished 过滤 */
export const tabToIsFinished = (tab?: PopReviewTabType): boolean | undefined =>
    tab === undefined ? undefined : tab === PopReviewTabType.TERMINATED;

/** 审核动作：纯人工判定 */
export enum PopReviewAction {
    APPROVE = 'APPROVE',
    REJECT = 'REJECT'
}

/** 该状态是否为终态 */
export const isPopReviewFinished = (status?: PopReviewStatus): boolean =>
    !!status && status !== PopReviewStatus.PENDING_REVIEW;

/**
 * 时间区间（前端筛选态）：持有 dayjs 对象，转查询入参时才规整为 ISO 字符串。
 * 后端为 `>= from` / `< to`（不含），详见 usePopReviewQuery。
 */
export type TimeRange = [Dayjs, Dayjs];

/** 多语言文案项 */
export interface LangTextEntry {
    /** BCP-47 short code：zh|en|es */
    lang: string;
    text: string;
}

/**
 * 不合格原因字典项；文案单一来源为服务端枚举，前端按 locale 自取，不拼 i18n key。
 * 服务端已排好序，顺序渲染即可。
 */
export interface PopDisqualifyReasonOption {
    code?: string;
    displayOrder: number;
    texts: LangTextEntry[];
    /** 所属分类 code；仅用于下拉分组展示，提交时仍只回传 code。空串表示不分组 */
    categoryCode: string;
    /** 分类多语言文案，用作下拉分组标题 */
    categoryTexts: LangTextEntry[];
}

/**
 * 工作台列表行
 *
 * 名称类字段三级兜底：后端回查的名称 → 下拉的 id→name 映射（司机名只有这一条路）→ 显示 ID。
 */
export interface PopReviewType {
    reviewTaskId: string;
    /** 入审场景 */
    reviewScene?: PopReviewScene;
    status: PopReviewStatus;
    /** 提交时间（epoch seconds） */
    submittedAt?: number;
    /** 审核完成截止时间（epoch seconds；SLA 超时仅告警，不改状态） */
    slaDueAt?: number;
    reviewerId?: string;
    /** 审核人信息（后端 DataLoader 回查 account）；未审核时为 null */
    reviewer?: {name?: string};
    /** 审核完成时间（epoch seconds） */
    reviewedAt?: number;
    /** 更新时间（epoch seconds）；列表排序与「更新时间」展示列同源 */
    updatedAt?: number;

    popReviewId?: string;
    appointmentId?: string;
    appointmentNumber?: string;
    /** 集散中心 ID（Hub） */
    pickupHubId?: string;
    /** 集散中心名称（后端 DataLoader 回查）；查不到为 null，前端回落显示 ID */
    pickupHubName?: string;
    /** 揽收站点 ID（合作仓） */
    pickupSiteId?: string;
    /** 揽收司机 account ID */
    driverId?: string;
    /** 揽收司机姓名（后端 DataLoader 回查 account）；查不到为 null，前端回落显示 ID */
    driverName?: string;
    actualParcelCount?: number;
    pickupFailureReasonCode?: string;

    /** POP 照片数量；URL 仅详情返回 */
    popPhotoCount: number;
    /** GPS 纬度；为空表示无定位信息（后端以 String 传输，避免浮点末位丢失） */
    pickupGpsLatitude?: string;
    pickupGpsLongitude?: string;
    /** GPS 采集时间（epoch seconds） */
    pickupGpsAt?: number;
    /** 揽收点与站点直线距离(米)；为空表示无法计算 */
    pickupSiteDistanceMeters?: number;

    /** 揽收侧字段（后端 DataLoader 批量回查）；取不到为 null */
    pickupDetail?: PopReviewPickupDetail;
    /** 不合格原因码；待审核单为空数组 */
    disqualifyReasonCodes: string[];
    /** 审核备注（原文 + 多语译文）；无备注为 null */
    reviewRemark?: PopReviewRemark;
}

/** 列表行的揽收侧字段（来自揽收服务） */
export interface PopReviewPickupDetail {
    siteName?: string;
    /** 已拼国家码 */
    contactPhone?: string;
    channelId?: string;
    channelName?: string;
    address?: string;
    zipcode?: string;
    timezone?: string;
    /** 原始值 SYSTEM / MANUAL / STANDING_PICKUP 等，文案由前端映射 */
    source?: string;
    /** 0-否，1-是 */
    isNewWarehouse?: number;
    routeName?: string;
    taskNumber?: string;
    /** 揽收状态原始值；文案用 enum.pickupAppointmentStatus.* 映射 */
    pickupStatus?: string;
    /** yyyy-MM-dd */
    expectedPickupDate?: string;
    /** HH:mm:ss */
    expectedPickupTimeStart?: string;
    /** epoch seconds */
    updatedAt?: number;
    /** 是否部分揽收：0-全部揽收，1-部分揽收 */
    isPartialPickup?: number;
}

/** 审核备注单条译文 */
export interface PopReviewRemarkTranslation {
    /** BCP-47 short code：zh|en|es */
    lang: string;
    text: string;
}

/**
 * 审核备注的原文与译文。
 *
 * 译文由后端异步补且允许丢失，取不到时必须回落 text —— 见 pickRemarkText。
 */
export interface PopReviewRemark {
    /** 原文；译文无命中时的兜底 */
    text: string;
    /** 原文语种 short code */
    lang: string;
    /** 译文列表，不含源语种；可为空数组 */
    translations: PopReviewRemarkTranslation[];
}

/**
 * 详情页揽收侧信息（PRD「站点信息」区块）。
 * 取数失败时后端返回 null，详情其余部分照常展示。
 */
export interface PopReviewPickupInfo {
    /** 站点名称（揽收侧直接给出，不依赖前端下拉映射） */
    siteName?: string;
    address?: string;
    zipcode?: string;
    /** 本次揽收的现场联系人（非客户公司常驻联系人） */
    contactName?: string;
    /** 已拼国家码，如 +1 9498876992 */
    contactPhone?: string;
    channelName?: string;
    taskNumber?: string;
    routeName?: string;
    isLongTermPickup?: boolean;
    /** 是否部分揽收：0-全部揽收，1-部分揽收 */
    isPartialPickup?: number;
    /** 部分揽收原因：1-部分货物未准备好，2-车辆装不下，3-其他 */
    partialPickupReason?: number;
    /** 预约件数 */
    expectedParcelCount?: number;
    /** 实际包装形态：1-件 2-袋 3-箱 4-板 */
    actualParcelUnit?: number;
}

/** 司机现场扫码明细一行：运单号与无法扫描原因二者有其一 */
export interface PopReviewScanRecord {
    trackingNumber?: string;
    unscannableReason?: string;
    /** 揽收侧整单提交时间（epoch 秒），同一预约单各行相同 */
    scannedAt?: number;
}

/** 揽收状态三态（不落库，前端推导） */
export enum PopPickupResult {
    ALL = 'ALL',
    PARTIAL = 'PARTIAL',
    NONE = 'NONE'
}

/**
 * 推导揽收状态：先看场景（失败单的 isPartialPickup 也是 0），成功单再看是否部分揽收。
 * 成功单取不到揽收侧数据时返回 undefined，不默认成全部揽收。
 */
export const getPopPickupResult = (
    scene?: PopReviewScene,
    isPartialPickup?: number
): PopPickupResult | undefined => {
    if (scene !== PopReviewScene.PICKUP_SUCCESS) {
        return PopPickupResult.NONE;
    }
    if (isPartialPickup == null) {
        return undefined;
    }
    return isPartialPickup === 1 ? PopPickupResult.PARTIAL : PopPickupResult.ALL;
};

/** 详情：列表行 + 揽收侧信息 + 照片 URL */
export interface PopReviewDetail {
    item: PopReviewType;
    /** 揽收侧信息；取数失败为 null，不影响详情其余部分 */
    pickupInfo?: PopReviewPickupInfo;
    /** 预签名 URL（有效期 10 分钟）；生成失败的已被后端过滤 */
    popPhotoUrls: string[];
    /** BOL 提货单图片预签名 URL（有效期 10 分钟）；无 BOL 为 null */
    bolPhotoUrl?: string;
    /** 司机现场扫码明细（按扫描先后）；无记录或取数失败为空数组 */
    scanRecords: PopReviewScanRecord[];
}

/** 审核结果 */
export interface PopReviewActionResult {
    reviewTaskId?: string;
    appointmentId?: string;
    appointmentNumber?: string;
    status: PopReviewStatus;
    reviewerId?: string;
    reviewedAt?: number;
}

/**
 * 筛选条件（前端态）
 *
 * 时间区间以 dayjs 元组持有，提交时拆成 xxxFrom / xxxTo 的 ISO-8601 字符串。
 */
export interface PopReviewFilters {
    /**
     * 场景与 Tab 由页面单独持有，不放入本对象。
     * 后端 filters 支持 reviewTaskId 精确匹配，但筛选区不暴露 —— 该 ID 对操作人员不可见。
     */
    statuses?: PopReviewStatus[];
    /** 预约单号，支持多值（分号 / 逗号 / 换行 分隔） */
    appointmentNumbers?: string;
    pickupHubId?: string;
    pickupSiteId?: string;
    driverId?: string;
    /** 不合格原因码多选；命中任一即返回 */
    disqualifyReasonCodes?: string[];
    submittedAt?: TimeRange;
    reviewedAt?: TimeRange;
}
